import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { insightsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import Toast from '../../components/common/Toast';

const InsightList = () => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchInsights();
  }, [currentPage, status, appliedSearch]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const response = await insightsAPI.getAll({ status, search: appliedSearch, page: currentPage, limit: 20 });
      if (response.data.success) {
        setInsights(response.data.data.insights);
        setTotalPages(response.data.data.pagination.total_pages);
      }
    } catch (error) {
      setToast({ show: true, message: 'Failed to fetch insights', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(search.trim());
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this insight?')) return;
    try {
      await insightsAPI.delete(id);
      setToast({ show: true, message: 'Insight deleted', type: 'success' });
      fetchInsights();
    } catch (error) {
      setToast({ show: true, message: 'Failed to delete', type: 'error' });
    }
  };

  return (
    <div>
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="page-title">Daily Insights</h2>
            <p className="page-subtitle">Manage daily inspiration content</p>
          </div>
          <Link to="/insights/new" className="btn btn-primary"><i className="bi bi-plus-circle me-2"></i>New Insight</Link>
        </div>
      </div>

      {/* Filters */}
      <div className="stat-card mb-4">
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label">Status</label>
            <select className="form-select" value={status} onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}>
              <option value="all">All Insights</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="col-md-7">
            <label className="form-label">Search</label>
            <form onSubmit={handleSearch}>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search title..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button className="btn btn-primary" type="submit">
                  <i className="bi bi-search"></i>
                </button>
              </div>
            </form>
          </div>
          <div className="col-md-2">
            <label className="form-label">&nbsp;</label>
            <button className="btn btn-outline-primary w-100" onClick={fetchInsights}>
              <i className="bi bi-arrow-clockwise me-2"></i>Refresh
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="data-table">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {insights.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                      <p className="text-muted mb-0">No insights found</p>
                    </td>
                  </tr>
                ) : (
                  insights.map((insight) => (
                    <tr key={insight.id}>
                      <td>
                        {insight.image_url ? (
                          <img
                            src={insight.image_url}
                            alt="Insight"
                            className="rounded"
                            style={{ width: '60px', height: '45px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            className="rounded d-flex align-items-center justify-content-center bg-secondary-subtle"
                            style={{ width: '60px', height: '45px' }}
                          >
                            <i className="bi bi-image text-muted"></i>
                          </div>
                        )}
                      </td>
                      <td>{insight.title}</td>
                      <td><span className="badge badge-purple">{insight.category_id}</span></td>
                      <td>{insight.display_order}</td>
                      <td>{Number(insight.is_active) === 1 ? <span className="badge badge-success">Active</span> : <span className="badge badge-danger">Inactive</span>}</td>
                      <td>
                        <div className="action-buttons">
                          <Link to={`/insights/edit/${insight.id}`} className="btn btn-sm btn-outline-primary"><i className="bi bi-pencil"></i></Link>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(insight.id)}><i className="bi bi-trash"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="p-3 border-top">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InsightList;